/**
 * @file Agreements.jsx
 * @description Handles legal agreements and document signing during onboarding
 *
 * @section Responsibilities
 * - Display legal agreements
 * - Handle electronic signatures
 * - Track agreement status
 * - Manage document acceptance
 * - Store signed documents
 * - Handle agreement updates
 *
 * @section Dependencies
 * - DocumentService: Agreement management
 * - SignatureService: Signature handling
 * - StorageService: Document storage
 * - LegalService: Legal compliance
 *
 * @section Integration Points
 * - Legal API: Agreement data
 * - Signature System: Electronic signatures
 * - Storage System: Document storage
 * - Onboarding Flow: Progress tracking
 *
 * @section State Management
 * - agreements: Agreement documents
 * - signatureStatus: Signature state
 * - acceptanceStatus: Acceptance state
 * - updates: Agreement updates
 *
 * @section Events
 * - onAgreementDisplay: Agreement viewing
 * - onSignature: Signature handling
 * - onAcceptance: Agreement acceptance
 * - onUpdate: Agreement updates
 *
 * @section Props
 * @prop {Array} requiredAgreements - List of required agreements
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialSignatures - Initial signature state
 * @prop {Boolean} allowUpdates - Controls agreement updates
 */

import axios from "axios";
import React, { useState, useEffect } from "react";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/strategy-selection.css";
import { useNavigate } from "react-router-dom";
import BallsBackground from "../../components/BallsBackground";

function useElementSize(element) {
  const [elementSize, setElementSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setElementSize({
        width: element.current ? element.current.offsetWidth : 0,
        height: element.current ? element.current.offsetHeight : 0,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return elementSize;
}

function Agreements() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.session);

  const [data, setData] = useState({
    strategy: "Bull",
  });

  const updateStrategy = (stg) => {
    setData({ strategy: stg });
  };

  const updateUser = () => {
    setLoading(true);

    axios
      .put(`${process.env.REACT_APP_BASE_URL}/user`, {
        PK: user.PK,
        SK: user.SK,
        ...data,
      })
      .then((res) => {
        navigate("/dashboard");
      });
  };

  return (
    <div className="strategy-selection">
      <BallsBackground>
        <div className="content container shadow1">
          <div className="progress-bar step2">
            <div className="progress"></div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>1</span>
                </div>
                <p>Your profile</p>
              </div>
            </div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>2</span>
                </div>
                <p>Agreements</p>
              </div>
            </div>
            <div className="step">
              <div className="title">
                <div className="number">
                  <span>3</span>
                </div>
                <p>Documents</p>
              </div>
            </div>
          </div>
          <div className="inner-content">
            <div className="heading">
              <p className="subtitle">Step 2</p>
              <h1 className="title">Agreements</h1>
              <p className="description">
                We'll send you a co-branded email allowing you to digitally sign
                the agreements. <br />
                Once completed, you'll be redirected back to our site.
              </p>
            </div>

            <img
              style={{ width: 280, marginLeft: "auto", marginRight: "auto" }}
              src="/assets/illos/hand2.png"
              alt=""
            />
            <p className="info">
              Please keep an eye on your inbox, and if you don't see the email
              within a few minutes, check your spam or junk folder. If you have
              any issues, feel free to contact our support team for assistance.
            </p>
          </div>
        </div>
      </BallsBackground>
    </div>
  );
}

export default Agreements;
